import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext(null);
const themeStorageKey = "app_theme_mode";

function getInitialTheme() {
  if (typeof window === "undefined") {
    return { theme: "light", useSystem: true };
  }
  const stored = localStorage.getItem(themeStorageKey);
  if (stored === "light" || stored === "dark") {
    return { theme: stored, useSystem: false };
  }
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  return { theme: prefersDark ? "dark" : "light", useSystem: true };
}

export function ThemeProvider({ children }) {
  const initial = useMemo(getInitialTheme, []);
  const [theme, setThemeState] = useState(initial.theme);
  const [useSystem, setUseSystem] = useState(initial.useSystem);
  const [forcedTheme, setForcedTheme] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined" || !useSystem) return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => setThemeState(media.matches ? "dark" : "light");
    if (media.addEventListener) media.addEventListener("change", apply);
    else media.addListener(apply);
    return () => {
      if (media.removeEventListener) media.removeEventListener("change", apply);
      else media.removeListener(apply);
    };
  }, [useSystem]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const appliedTheme = forcedTheme || theme;
    root.dataset.theme = appliedTheme;
    root.style.colorScheme = appliedTheme;
    document.body.style.background = "var(--cr-bg)";
    document.body.style.color = "var(--cr-text)";
  }, [theme, forcedTheme]);

  const setTheme = (nextTheme) => {
    setThemeState(nextTheme);
    setUseSystem(false);
    localStorage.setItem(themeStorageKey, nextTheme);
  };

  const toggleTheme = () => {
    setThemeState((current) => {
      const nextTheme = current === "dark" ? "light" : "dark";
      setUseSystem(false);
      localStorage.setItem(themeStorageKey, nextTheme);
      return nextTheme;
    });
  };

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      useSystem,
      forcedTheme,
      setForcedTheme,
    }),
    [theme, useSystem, forcedTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
}
